import { sessionQueryOptions } from '@pwa/queries/auth/session';
import type { QueryClient } from '@tanstack/react-query';

/**
 * Auth guard helper.
 *
 * Returns the cached session if one is available, otherwise `null`.
 * Designed to be used inside a TanStack Router `beforeLoad` so routes
 * can opt into protected rendering declaratively.
 *
 * Redirect behavior is *not* performed here — the route (or the layout
 * route) is responsible for calling `throw redirect(...)` with the
 * correct destination. Keeping the redirect in the caller means:
 *   1. Each route can choose where to send unauthenticated users
 *      (`/login`, `/signup`, etc.) without `requireSession` knowing
 *      about route topology.
 *   2. The same helper is reusable for both "soft" checks (show a
 *      "please sign in" panel) and "hard" redirects.
 *   3. Errors are intentionally swallowed: a failed session fetch is
 *      treated as "no session" rather than an exception so the route
 *      can decide whether to redirect or render an error state.
 *
 * Example:
 *
 *   beforeLoad: async ({ context, location }) => {
 *     const session = await requireSession(context.queryClient);
 *     if (!session) throw redirect({ to: '/login', replace: true });
 *   }
 */
export async function requireSession(queryClient: QueryClient) {
	try {
		const result = await queryClient.fetchQuery(sessionQueryOptions);
		return result.data;
	} catch {
		return null;
	}
}
